import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Smartphone, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WalletButtons = ({ passType, entityId, compact = false, showPreview = false }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wallet/status`)
      .then(r => r.json())
      .then(d => setStatus(d))
      .catch(() => {});
  }, []);

  const appleReady = status?.apple_wallet?.configured;
  const googleReady = status?.google_wallet?.configured;

  const downloadApplePass = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/wallet/apple/${passType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ultraflex_${passType}.pkpass`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  const openGoogleWallet = async () => {
    try {
      const res = await fetch(`${API}/api/wallet/google/${passType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.open(data.url, '_blank');
        } else if (!data.configured) {
          setShowInfo(true);
        }
      }
    } catch (e) { console.error(e); }
  };

  const loadPreview = async () => {
    try {
      const res = await fetch(`${API}/api/wallet/preview/${passType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) setPreview(await res.json());
    } catch (e) { console.error(e); }
  };

  const notConfigured = status && !appleReady && !googleReady;

  if (compact) {
    return (
      <div className="flex flex-col gap-2" data-testid={`wallet-buttons-${passType}`}>
        <div className="flex gap-2">
          <button
            onClick={downloadApplePass}
            disabled={downloading}
            className="flex items-center gap-1.5 bg-black text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-zinc-800 transition-colors border border-zinc-700"
            data-testid={`apple-wallet-btn-${passType}`}
            title="Add to Apple Wallet"
          >
            <AppleIcon className="w-4 h-4" />
            {downloading ? 'Loading...' : 'Apple Wallet'}
          </button>
          <button
            onClick={openGoogleWallet}
            className="flex items-center gap-1.5 bg-white text-black px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors"
            data-testid={`google-wallet-btn-${passType}`}
            title="Add to Google Wallet"
          >
            <GoogleIcon className="w-4 h-4" />
            Google Wallet
          </button>
        </div>
        {notConfigured && <p className="text-amber-400/70 text-[10px] flex items-center gap-1"><Info className="w-3 h-3" />Wallet credentials pending setup</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`wallet-section-${passType}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-uf-text flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-uf-red" />
          Add to Wallet
        </h4>
        {notConfigured && (
          <span className="text-[10px] text-amber-400/70 bg-amber-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Info className="w-3 h-3" /> Awaiting credentials
          </span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={downloadApplePass}
          disabled={downloading}
          className="flex-1 flex items-center justify-center gap-2.5 bg-black text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-zinc-800 transition-all border border-zinc-700 shadow-lg"
          data-testid={`apple-wallet-btn-${passType}`}
        >
          <AppleIcon className="w-5 h-5" />
          <span>{downloading ? 'Generating...' : 'Add to Apple Wallet'}</span>
        </button>

        <button
          onClick={openGoogleWallet}
          className="flex-1 flex items-center justify-center gap-2.5 bg-white text-[#1f1f1f] px-4 py-3 rounded-xl text-sm font-medium hover:bg-gray-100 transition-all shadow-lg"
          data-testid={`google-wallet-btn-${passType}`}
        >
          <GoogleIcon className="w-5 h-5" />
          <span>Add to Google Wallet</span>
        </button>
      </div>

      {showInfo && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
          <p>Google Wallet credentials are not yet configured. Contact your administrator to set up the Google Wallet integration.</p>
          <button onClick={() => setShowInfo(false)} className="text-amber-400 underline mt-1">Dismiss</button>
        </div>
      )}

      {showPreview && (
        <button
          onClick={loadPreview}
          className="text-xs text-uf-text-muted hover:text-uf-red transition-colors"
          data-testid="preview-pass-btn"
        >
          Preview pass data
        </button>
      )}

      {preview && (
        <PassPreview data={preview.pass_data} onClose={() => setPreview(null)} />
      )}
    </div>
  );
};

/* Pass Preview Card */
const PassPreview = ({ data, onClose }) => (
  <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] rounded-2xl border border-zinc-800 overflow-hidden shadow-2xl" data-testid="pass-preview">
    {/* Top strip */}
    <div className="h-2" style={{ backgroundColor: data.label_color || '#E31837' }} />

    <div className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-lg font-bold text-white tracking-wide">{data.title}</p>
          <p className="text-xs text-zinc-400">{data.subtitle}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: data.label_color || '#E31837' }}>
          UF
        </div>
      </div>

      {/* Primary Fields */}
      {data.primary_fields?.map((f, i) => (
        <div key={i} className="mb-3">
          <p className="text-[10px] uppercase tracking-wider" style={{ color: data.label_color || '#E31837' }}>{f.label}</p>
          <p className="text-xl font-bold text-white">{f.value}</p>
        </div>
      ))}

      {/* Secondary Fields */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {data.secondary_fields?.map((f, i) => (
          <div key={i}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: data.label_color || '#E31837' }}>{f.label}</p>
            <p className="text-sm text-white font-medium">{f.value}</p>
          </div>
        ))}
      </div>

      {/* Auxiliary Fields */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {data.auxiliary_fields?.map((f, i) => (
          <div key={i}>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{f.label}</p>
            <p className="text-xs text-zinc-300">{f.value}</p>
          </div>
        ))}
      </div>

      {/* QR Code placeholder */}
      <div className="bg-white rounded-xl p-4 flex flex-col items-center">
        <div className="w-32 h-32 bg-zinc-100 rounded-lg flex items-center justify-center mb-2">
          <svg viewBox="0 0 100 100" className="w-24 h-24">
            <rect x="10" y="10" width="30" height="30" fill="#111" rx="2" />
            <rect x="60" y="10" width="30" height="30" fill="#111" rx="2" />
            <rect x="10" y="60" width="30" height="30" fill="#111" rx="2" />
            <rect x="15" y="15" width="8" height="8" fill="#111" />
            <rect x="65" y="15" width="8" height="8" fill="#111" />
            <rect x="15" y="65" width="8" height="8" fill="#111" />
            <rect x="27" y="15" width="8" height="8" fill="#111" />
            <rect x="77" y="15" width="8" height="8" fill="#111" />
            <rect x="27" y="65" width="8" height="8" fill="#111" />
            <rect x="45" y="45" width="10" height="10" fill="#111" rx="1" />
            <rect x="60" y="60" width="8" height="8" fill="#111" />
            <rect x="75" y="60" width="8" height="8" fill="#111" />
            <rect x="60" y="75" width="8" height="8" fill="#111" />
          </svg>
        </div>
        <p className="text-xs text-zinc-600 font-mono">{data.barcode_alt || data.barcode_value}</p>
      </div>
    </div>

    <button onClick={onClose} className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-300 border-t border-zinc-800">
      Close Preview
    </button>
  </div>
);

/* Apple Logo SVG */
const AppleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

/* Google Wallet Logo SVG */
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/* Public Day Pass Wallet Buttons (no auth needed) */
export const DayPassWalletButtons = ({ retrievalCode, compact = false }) => {
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/wallet/status`)
      .then(r => r.json())
      .then(d => setStatus(d))
      .catch(() => {});
  }, []);

  const notConfigured = status && !status.apple_wallet?.configured && !status.google_wallet?.configured;
  const dayPassId = retrievalCode; // Actually the day pass ID

  const downloadApplePass = async () => {
    if (dayPassId) {
      setDownloading(true);
      try {
        const res = await fetch(`${API}/api/wallet/public/day-pass/${dayPassId}?provider=apple`);
        if (res.ok) {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ultraflex_daypass.pkpass';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (e) { console.error(e); }
      finally { setDownloading(false); }
    }
  };

  const openGoogleWallet = async () => {
    if (dayPassId) {
      try {
        const res = await fetch(`${API}/api/wallet/public/day-pass/${dayPassId}?provider=google`);
        if (res.ok) {
          const data = await res.json();
          if (data.url) window.open(data.url, '_blank');
          else if (!data.configured) setShowInfo(true);
        }
      } catch (e) { console.error(e); }
    }
  };

  if (compact) {
    return (
      <div className="flex flex-col gap-2" data-testid="daypass-wallet-buttons">
        <div className="flex gap-2">
          <button onClick={downloadApplePass} disabled={downloading || !dayPassId}
            className="flex items-center gap-1.5 bg-black text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-zinc-800 border border-zinc-700 disabled:opacity-50"
            data-testid="daypass-apple-wallet-btn">
            <AppleIcon className="w-4 h-4" />{downloading ? '...' : 'Apple Wallet'}
          </button>
          <button onClick={openGoogleWallet} disabled={!dayPassId}
            className="flex items-center gap-1.5 bg-white text-black px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
            data-testid="daypass-google-wallet-btn">
            <GoogleIcon className="w-4 h-4" />Google Wallet
          </button>
        </div>
        {notConfigured && <p className="text-amber-400/70 text-[10px] flex items-center gap-1"><Info className="w-3 h-3" />Wallet credentials pending setup</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="daypass-wallet-section">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-[#e63946]" />
          Add to Wallet
        </h4>
        {notConfigured && (
          <span className="text-[10px] text-amber-400/70 bg-amber-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Info className="w-3 h-3" /> Awaiting credentials
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={downloadApplePass} disabled={downloading || !dayPassId}
          className="flex-1 flex items-center justify-center gap-2.5 bg-black text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-zinc-800 border border-zinc-700 shadow-lg disabled:opacity-50"
          data-testid="daypass-apple-wallet-btn">
          <AppleIcon className="w-5 h-5" /><span>{downloading ? 'Generating...' : 'Add to Apple Wallet'}</span>
        </button>
        <button onClick={openGoogleWallet} disabled={!dayPassId}
          className="flex-1 flex items-center justify-center gap-2.5 bg-white text-[#1f1f1f] px-4 py-3 rounded-xl text-sm font-medium hover:bg-gray-100 shadow-lg disabled:opacity-50"
          data-testid="daypass-google-wallet-btn">
          <GoogleIcon className="w-5 h-5" /><span>Add to Google Wallet</span>
        </button>
      </div>
      {showInfo && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
          <p>Google Wallet credentials are not yet configured.</p>
          <button onClick={() => setShowInfo(false)} className="text-amber-400 underline mt-1">Dismiss</button>
        </div>
      )}
    </div>
  );
};

export default WalletButtons;
